import ApiService from "./ApiService";
import SafeLocalStorage from "../utils/SafeLocalStorage";

interface LoginCredentials {
  password: string;
  email: string;
}

interface User {
  username: string;
  // Add other user properties as needed
}

class AuthService {
  private static readonly AUTH_TOKEN_KEY = "auth-token";
  private static readonly USER_KEY = "user";

  // Check if user is authenticated
  static isAuthenticated(): boolean {
    return !!SafeLocalStorage.getItem(this.AUTH_TOKEN_KEY);
  }

  // Get current user
  static getCurrentUser(): User | null {
    return SafeLocalStorage.getItem(this.USER_KEY);
  }

  // Login user
  static async login(credentials: LoginCredentials): Promise<User> {
    try {
      const response = await ApiService.post("/user/login", credentials);
      const data = await response.json();

      // Store auth token and user data in SafeLocalStorage
      SafeLocalStorage.setItem(this.AUTH_TOKEN_KEY, data.token);
      SafeLocalStorage.setItem(this.USER_KEY, data);

      return data;
    } catch (error) {
      console.error("Login failed:", error);

      // Handle different error status codes
      const status = (error as any).status;

      if (status === 401) {
        throw new Error(
          "Authentication failed. Please check your email and password.",
        );
      } else if (status === 429) {
        throw new Error("Too many login attempts. Please try again later.");
      } else if (status >= 500 && status < 600) {
        throw new Error(
          "Server error. Please try again later or contact support.",
        );
      } else {
        throw new Error("Login failed. Please try again.");
      }
    }
  }

  // Logout user
  static logout(): void {
    SafeLocalStorage.removeItem(this.AUTH_TOKEN_KEY);
    SafeLocalStorage.removeItem(this.USER_KEY);
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  }

  // Get auth token for API requests
  static getAuthToken(): string | null {
    return SafeLocalStorage.getItem(this.AUTH_TOKEN_KEY);
  }
}

export default AuthService;
